import React, { useEffect } from 'react';
import { ArrowLeft, Zap, Clock, DollarSign, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LaserRemoval = () => {
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Back Button */}
      <div className="fixed top-24 left-8 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm tracking-wider">BACK TO HOME</span>
        </button>
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-6">
            LASER TATTOO REMOVAL
          </h1>
          <p className="text-xl text-white/80 tracking-wider mb-8">
            Professional removal with advanced Q-Switched Nd:YAG Picosecond laser technology
          </p>
          <div className="flex items-center justify-center gap-3 text-white/90 mb-12">
            <Zap className="text-white" size={24} />
            <span className="text-sm tracking-wider">HIGH-END EQUIPMENT | FEWER TREATMENTS | GREAT RESULTS</span>
          </div>
        </div>
      </section>

      {/* Laser Equipment & Procedure Photos */}
      <section className="py-12 px-4 bg-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative h-[500px] overflow-hidden">
              <img
                src="https://res.cloudinary.com/ddclgpokn/image/upload/v1776092406/IMG_3115_zy07jb.jpg"
                alt="PicoTech Q-Switched Nd:YAG Picosecond laser tattoo removal equipment Atlantic Coast Tattoo Newquay Cornwall - professional laser removal machine"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-4 left-4 bg-black/80 px-4 py-2">
                <span className="text-white tracking-wider text-sm">OUR EQUIPMENT</span>
              </div>
            </div>
            <div className="relative h-[500px] overflow-hidden">
              <img
                src="https://res.cloudinary.com/ddclgpokn/image/upload/v1776100560/IMG_3121_k1wdqn.jpg"
                alt="Professional laser tattoo removal procedure in progress at Atlantic Coast Tattoo Cornwall - Jerry performing laser removal treatment on client Newquay"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-4 left-4 bg-black/80 px-4 py-2">
                <span className="text-white tracking-wider text-sm">PROCEDURE IN ACTION</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white tracking-widest mb-12 text-center">
            HOW IT WORKS
          </h2>

          <div className="space-y-8">
            <div className="bg-white/5 p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Why does the ink stay in the skin?</h3>
              <p className="text-white/80 leading-relaxed">
                When you have a tattoo, the particles of ink are too large for the body to destroy. Instead, it encapsulates the particles of ink with a network of collagen fibres as the skin heals, and there the tattoo remains.
              </p>
            </div>

            <div className="bg-white/5 p-8">
              <h3 className="text-2xl font-bold text-white mb-4">How does the YAG Picosecond laser work?</h3>
              <p className="text-white/80 leading-relaxed">
                The laser emits a pulse of light that is preferentially absorbed into certain colours of ink. The light enters the skin at a speed that is too fast to heat the tissues (billionths of a second), instead producing a 'shock wave' effect that breaks up the ink particles into smaller pieces. The body then recognises these particles as something that shouldn't be there and attacks it. The particles are then removed naturally via the body's own elimination system.
              </p>
            </div>

            <div className="bg-white/5 p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Can you treat all colours?</h3>
              <p className="text-white/80 leading-relaxed">
                The laser has two wavelengths: invisible infrared light (1064nm) absorbed by black and dark blue inks, and visible green light (532nm) absorbed by red ink. White, yellow, and bright colours are difficult to treat. However, this is not usually a problem as lighter colours cover very easily. If you wish to cover-up an existing tattoo, the darker colours will be faded or removed, allowing greater choice of size and colour for your new design.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Information */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-white tracking-widest mb-12 text-center">
            KEY INFORMATION
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/50 p-8">
              <div className="flex items-start gap-4 mb-4">
                <DollarSign className="text-white mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Pricing</h3>
                  <p className="text-white/80 leading-relaxed">
                    From £30 per session for small tattoos. Maximum £90 for areas up to half A4 size. Initial consultations and test patches are FREE.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-black/50 p-8">
              <div className="flex items-start gap-4 mb-4">
                <Clock className="text-white mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Treatment Timeline</h3>
                  <p className="text-white/80 leading-relaxed">
                    Treatments scheduled 6 weeks apart. Amateur tattoos may take 3 treatments, professional tattoos up to 10 treatments for complete removal.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-black/50 p-8">
              <div className="flex items-start gap-4 mb-4">
                <Heart className="text-white mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Aftercare</h3>
                  <p className="text-white/80 leading-relaxed">
                    Skin will be red and feel like mild sunburn for a couple of hours. Possible bruising and mild blistering that heals within a week. Detailed aftercare instructions provided.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-black/50 p-8">
              <div className="flex items-start gap-4 mb-4">
                <Zap className="text-white mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Pain Level</h3>
                  <p className="text-white/80 leading-relaxed">
                    Feels like a rubber band snapping quickly on your skin. Very quick procedure that normally takes minutes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white tracking-widest mb-12 text-center">
            FREQUENTLY ASKED QUESTIONS
          </h2>

          <div className="space-y-6">
            <div className="border-b border-white/20 pb-6">
              <h3 className="text-xl font-bold text-white mb-3">Does it leave scars?</h3>
              <p className="text-white/80 leading-relaxed">
                The type of laser used leaves virtually no scars. However, in rare cases, those with scarring disorders such as keloids have an increased risk of forming a scar.
              </p>
            </div>

            <div className="border-b border-white/20 pb-6">
              <h3 className="text-xl font-bold text-white mb-3">Who performs the treatment?</h3>
              <p className="text-white/80 leading-relaxed">
                The laser tattoo removal is performed by Jerry Dzeraldas Kudrevičius, who is fully trained, certified, and insured to perform this procedure.
              </p>
            </div>

            <div className="border-b border-white/20 pb-6">
              <h3 className="text-xl font-bold text-white mb-3">Why is it difficult to treat colours other than red and black?</h3>
              <p className="text-white/80 leading-relaxed">
                Different wavelengths absorb into different colours. Some wavelengths may be too shallow or can damage the skin. Heavy metals in ink pigments can react with laser light and carbonise. We perform test patches for other colours to determine if treatment is possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white tracking-widest mb-12">
            SEE IT IN ACTION
          </h2>
          <div className="aspect-video bg-black/50">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/jl6v4Psz-qY"
              title="Atlantic Coast Laser Tattoo Removal"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white tracking-widest mb-6">
            READY TO START YOUR REMOVAL JOURNEY?
          </h2>
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            Get a free consultation and quote. Email us with a clear photo of your tattoo to discuss your options and appointment availability.
          </p>
          <div className="space-y-4">
            <a
              href="mailto:studio@atlanticcoasttattoo.com"
              className="inline-block bg-white text-black px-8 py-4 text-sm tracking-widest hover:bg-gray-200 transition-colors"
            >
              EMAIL FOR FREE CONSULTATION
            </a>
            <div className="text-white/70 text-sm">
              <p>Phone: 01637 498050</p>
              <p className="mt-2">
                Follow us on Instagram:{' '}
                <a
                  href="https://www.instagram.com/tattoo_removal_newquay/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-300 transition-colors"
                >
                  @tattoo_removal_newquay
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LaserRemoval;
